import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import SaveIcon from '@mui/icons-material/Save';
import dayjs from 'dayjs';
import { FLOW_STATUS_META } from './blockCatalog';

const GOAL_TYPE_LABELS = {
  none: 'Kein Ziel',
  booking: 'Buchung innerhalb des Zeitfensters',
  voucherRedemption: 'Gutschein-Einlösung innerhalb des Zeitfensters',
};

const defaultSettings = () => ({
  testMode: { enabled: false, allowedEmails: [] },
  reEntry: { mode: 'once', cooldownDays: 90 },
  goal: { type: 'none', windowDays: 30, entertainmentIds: [] },
});

const mergeSettings = incoming => {
  const base = defaultSettings();
  if (!incoming) return base;
  return {
    testMode: { ...base.testMode, ...(incoming.testMode || {}) },
    reEntry: { ...base.reEntry, ...(incoming.reEntry || {}) },
    goal: { ...base.goal, ...(incoming.goal || {}) },
  };
};

const formatDateTime = value => (value ? dayjs(value).format('DD.MM.YYYY HH:mm') : '—');

const MetaRow = ({ label, value }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: 0.5 }}>
    <Typography variant='body2' color='text.secondary'>
      {label}
    </Typography>
    <Typography variant='body2'>{value}</Typography>
  </Box>
);

// Gear dialog: flow status + metadata, activate ("veröffentlichen") and pause
// actions, and the German validation issues when activation is not possible.
const FlowSettingsDialog = ({
  open,
  onClose,
  flowMeta,
  canActivate,
  canEdit = false,
  busy,
  issues,
  onActivate,
  onPause,
  onIssueClick,
  onSaveSettings,
}) => {
  const status = flowMeta?.status || 'draft';
  const statusMeta = FLOW_STATUS_META[status] || FLOW_STATUS_META.draft;
  const isActive = status === 'active';

  const [settings, setSettings] = useState(mergeSettings(flowMeta?.settings));
  const [settingsDirty, setSettingsDirty] = useState(false);
  const [settingsBusy, setSettingsBusy] = useState(false);

  // Re-seed the local form only on the open transition, so a background
  // flowMeta refresh (e.g. after saving the canvas) never discards unsaved
  // edits in the settings form while the dialog is open.
  useEffect(() => {
    if (open) {
      setSettings(mergeSettings(flowMeta?.settings));
      setSettingsDirty(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const patchSettings = (section, partial) => {
    setSettings(previous => ({
      ...previous,
      [section]:
        partial && typeof partial === 'object' && !Array.isArray(partial)
          ? { ...previous[section], ...partial }
          : partial,
    }));
    setSettingsDirty(true);
  };

  const handleSaveSettings = async () => {
    if (!onSaveSettings) return;
    setSettingsBusy(true);
    try {
      await onSaveSettings(settings);
      setSettingsDirty(false);
    } catch {
      // The parent surfaces the error toast and re-throws; swallow here so the
      // button's onClick never triggers an unhandled promise rejection.
    } finally {
      setSettingsBusy(false);
    }
  };

  const actionButton = isActive ? (
    <Button
      color='warning'
      variant='contained'
      startIcon={<PauseIcon />}
      disabled={!canActivate || busy}
      onClick={onPause}
    >
      Flow pausieren
    </Button>
  ) : (
    <Button
      variant='contained'
      startIcon={<PlayArrowIcon />}
      disabled={!canActivate || busy}
      onClick={onActivate}
    >
      Flow veröffentlichen
    </Button>
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth>
      <DialogTitle>Flow-Einstellungen</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.5 }}>
          <Typography variant='body2' color='text.secondary'>
            Status
          </Typography>
          <Chip size='small' label={statusMeta.label} color={statusMeta.color} />
        </Box>
        <MetaRow label='Kontakte im Flow' value={String(flowMeta?.contactsInFlow ?? 0)} />
        <MetaRow label='Aktiviert am' value={formatDateTime(flowMeta?.activatedAt)} />
        <MetaRow label='Letzte Prüfung' value={formatDateTime(flowMeta?.lastCheckAt)} />
        <MetaRow label='Nächster geplanter Versand' value={formatDateTime(flowMeta?.nextScheduledSendAt)} />
        <Typography variant='caption' color='text.secondary' sx={{ display: 'block', mt: 1 }}>
          {isActive
            ? 'Beim Pausieren wird die Automation gestoppt, der Flow bleibt erhalten.'
            : 'Beim Veröffentlichen wird der Flow geprüft und anschließend aktiviert.'}
        </Typography>

        <Divider sx={{ my: 1.5 }} />
        <Typography variant='subtitle2' sx={{ mb: 1 }}>
          Verhalten
        </Typography>

        <FormControlLabel
          control={
            <Switch
              checked={Boolean(settings.testMode.enabled)}
              disabled={!canEdit}
              onChange={event => patchSettings('testMode', { enabled: event.target.checked })}
            />
          }
          label='Testmodus (Soft-Launch: nur Test-Adressen durchlaufen den Flow)'
        />
        {settings.testMode.enabled && (
          <TextField
            size='small'
            fullWidth
            label='Test-E-Mail-Adressen (kommagetrennt)'
            value={(settings.testMode.allowedEmails || []).join(', ')}
            disabled={!canEdit}
            onChange={event =>
              patchSettings('testMode', {
                allowedEmails: event.target.value
                  .split(',')
                  .map(entry => entry.trim())
                  .filter(Boolean),
              })
            }
            sx={{ mb: 1, mt: 0.5 }}
          />
        )}

        <Box sx={{ display: 'flex', gap: 1, mt: 1.5 }}>
          <TextField
            select
            size='small'
            label='Wiedereintritt'
            value={settings.reEntry.mode}
            disabled={!canEdit}
            onChange={event => patchSettings('reEntry', { mode: event.target.value })}
            sx={{ flex: 1 }}
          >
            <MenuItem value='once'>Einmalig (Standard)</MenuItem>
            <MenuItem value='cooldown'>Nach Cooldown erneut möglich</MenuItem>
          </TextField>
          {settings.reEntry.mode === 'cooldown' && (
            <TextField
              size='small'
              type='number'
              label='Cooldown (Tage)'
              value={settings.reEntry.cooldownDays}
              disabled={!canEdit}
              inputProps={{ min: 1 }}
              onChange={event =>
                patchSettings('reEntry', {
                  cooldownDays: Math.max(1, Number(event.target.value) || 1),
                })
              }
              sx={{ width: 150 }}
            />
          )}
        </Box>

        <Divider sx={{ my: 1.5 }} />
        <Typography variant='subtitle2' sx={{ mb: 1 }}>
          Conversion-Ziel
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <TextField
            select
            size='small'
            label='Ziel'
            value={settings.goal.type}
            disabled={!canEdit}
            onChange={event => patchSettings('goal', { type: event.target.value })}
            sx={{ flex: 1 }}
          >
            {Object.entries(GOAL_TYPE_LABELS).map(([value, label]) => (
              <MenuItem key={value} value={value}>
                {label}
              </MenuItem>
            ))}
          </TextField>
          {settings.goal.type !== 'none' && (
            <TextField
              size='small'
              type='number'
              label='Zeitfenster (Tage)'
              value={settings.goal.windowDays}
              disabled={!canEdit}
              inputProps={{ min: 1 }}
              onChange={event =>
                patchSettings('goal', {
                  windowDays: Math.max(1, Number(event.target.value) || 1),
                })
              }
              sx={{ width: 160 }}
            />
          )}
        </Box>
        {settings.goal.type !== 'none' && (
          <Typography variant='caption' color='text.secondary' sx={{ display: 'block', mt: 0.5 }}>
            Conversion-Rate und zugeordneter Umsatz erscheinen in den Funnel-KPIs — auch getrennt
            nach A/B-Varianten.
          </Typography>
        )}

        {canEdit && (
          <Button
            variant='outlined'
            size='small'
            startIcon={<SaveIcon />}
            disabled={!settingsDirty || settingsBusy}
            onClick={handleSaveSettings}
            sx={{ mt: 1.5 }}
          >
            Einstellungen speichern
          </Button>
        )}
        {issues.length > 0 && (
          <>
            <Divider sx={{ my: 1.5 }} />
            <Typography variant='subtitle2' color='error' sx={{ mb: 0.5 }}>
              Der Flow kann noch nicht aktiviert werden:
            </Typography>
            <List dense disablePadding>
              {issues.map((issue, index) => (
                <ListItemButton
                  key={`${issue.code || 'issue'}-${index}`}
                  onClick={() => onIssueClick(issue)}
                  sx={{ borderRadius: 1 }}
                >
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <ErrorOutlineIcon color='error' fontSize='small' />
                  </ListItemIcon>
                  <ListItemText primary={issue.message} />
                </ListItemButton>
              ))}
            </List>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Schließen</Button>
        {canActivate ? (
          actionButton
        ) : (
          <Tooltip title='Du benötigst das Recht "emailMarketing:activate".'>
            <span>{actionButton}</span>
          </Tooltip>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default FlowSettingsDialog;
